/* eslint-disable @typescript-eslint/member-ordering */
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatRadioChange } from '@angular/material/radio';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { SplitViewService } from '@ebw-monorepo/utils';
import { Feature } from 'ol';
import { CustomerEnum } from '../../../../../../utils/src/lib/enums/customer.enum';
import { FeatureNode, NODE_TYPES } from '../../../models/feature-note';
import { FeatureSelectService } from '../../../services/feature-select.service';
import { FeatureViewerService } from '../../../services/feature-viewer.service';
import { MapService } from '../../../services/map.service';
import { PsaService } from '../../../services/psa.service';
import { TelcoService } from '../../../services/telco.service';

interface ExampleFlatNode {
  expandable: boolean;
  name: string;
  level: number;
  nodeData: unknown;
  type: string;
  id: string;
}

@Component({
  selector: 'ebw-monorepo-feature-viewer-tree',
  templateUrl: './feature-viewer-tree.component.html',
  styleUrls: ['./feature-viewer-tree.component.scss']
})
export class FeatureViewerTreeComponent implements OnInit, OnDestroy, OnChanges {
  @Input() features: Feature[];
  @Input() simple: boolean = false;

  @Output() nodeSecondaryAction = new EventEmitter<{ node: FeatureNode; action: string }>();
  @Output() nodeSelected = new EventEmitter<FeatureNode>();

  customerEnum = CustomerEnum;

  public NODE_TYPES = NODE_TYPES;
  public selectedNode: FeatureNode = null;

  private _transformer = (node: FeatureNode, level: number) => {
    let data = {};

    if (node.nodeData && node.type === NODE_TYPES.FEATURE) {
      data = node.nodeData;
    }

    return {
      expandable: !!node.children && node.children.length > 0,
      level: level,
      ...node
    };
  };

  treeControl = new FlatTreeControl<ExampleFlatNode>(
    (node) => node.level,
    (node) => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  hasChild = (_: number, node: ExampleFlatNode) => node.expandable;

  constructor(
    public _psaService: PsaService,
    private _featureSelectService: FeatureSelectService,
    private _featureViewerService: FeatureViewerService,
    private _splitViewService: SplitViewService,
    private _mapService: MapService,
    private _telcoService: TelcoService
  ) {}
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.features) {
      this.selectedNode = null;
      const newFeatures = changes.features.currentValue;

      if (this.simple) {
        this.dataSource.data = this.features.map((feature) => {
          return {
            name: feature.getId(),
            id: feature.getId(),
            type: 'FEATURE',
            nodeData: feature,
            children: [],
            expandable: false,
            level: 0
          };
        }) as FeatureNode[];
      } else {
        this.dataSource.data = this._mappedTree(newFeatures);
      }

      if (!newFeatures?.length) {
        this._splitViewService.layoutConfChange({
          isEnable: false,
          halfScreen: true,
          collapsed: false
        });
      }
      this.treeControl.expandAll();
      this._checkOneNodeFeatureAndAutoSelect(this.dataSource.data);
    }
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this._featureViewerService.clearFeatures();
    this._featureSelectService.featuresViewerClosedSubject$.next(true);
    this._mapService.clearAllFeaturesFromTemporaryLayer();
  }

  public onSelectedNode(node: FeatureNode) {
    this.selectedNode = node;
    this.nodeSelected.emit(node);
  }

  public onSelectedNodeFromRadioButton(event: MatRadioChange) {
    this.onSelectedNode(event.value);
  }

  public onNodeAction(node, action) {
    this.nodeSecondaryAction.emit({ node, action });
  }

  private _mappedTree(features: Feature[]): FeatureNode[] {
    const dictonary: Record<string, any> = {};

    features.forEach((feature) => {
      const featureId = feature.getId();
      const { dataset_name, collection_name, collection_external_name, dataset_external_name } =
        this._featureSelectService.getConfigDataFromUrn(featureId);
      if (!dictonary[dataset_name]) {
        const datasetNode = {
          name: dataset_external_name,
          id: dataset_name,
          type: NODE_TYPES.DATASET,
          nodeData: {},
          children: [],
          collections_: {}
        };

        dictonary[dataset_name] = datasetNode;
      }

      if (!dictonary[dataset_name].collections_[collection_name]) {
        const collectionNode = {
          name: collection_external_name,
          id: collection_name,
          type: NODE_TYPES.COLLECTION,
          nodeData: {},
          children: []
        };

        dictonary[dataset_name].collections_[collection_name] = collectionNode;
      }
      const splittedIdName = featureId.toString().split('Z');

      const featureNode = {
        name: splittedIdName.pop(),
        id: featureId,
        type: NODE_TYPES.FEATURE,
        nodeData: feature
      };

      dictonary[dataset_name].collections_[collection_name].children.push(featureNode);
    });
    const tree = [];
    Object.values(dictonary).forEach((dataset: FeatureNode) => {
      dataset.children = Object.values(dataset['collections_']);
      delete dataset['collections_'];

      tree.push(dataset);
    });

    return tree;
  }

  private _checkOneNodeFeatureAndAutoSelect(tree: FeatureNode[]) {
    if (this.simple && tree.length === 1) {
      this.onSelectedNode(tree[0]);
      return;
    }

    //non uso la ricorsione perchè so che l'albero avrà sempre massimo 2 livelli
    // so che in questo caso devo avere un solo dataset, una sola collection e un solo nodo
    if (tree.length === 1 && tree[0].children.length === 1) {
      const collectionNode = tree[0].children[0];
      if (collectionNode.children.length === 1) {
        const child = collectionNode.children[0];
        //ok esiste solo un nodo feature
        this.onSelectedNode(child);
      }
    }
  }

  isAssetAllowedToDownloadKmzCircuiti = (feature: Feature): boolean =>
    this._telcoService.isAssetAllowedToDownloadKmzCircuiti(feature.getId().toString());

  isAssetAllowedToViewRaccordi = (feature: Feature): boolean => this._telcoService.isAssetAllowedToViewRaccordi(feature.getId().toString());
}
